import { useMutation, useQuery } from '~/shared/hooks/query'
import { useQueryClient } from '@tanstack/react-query'
import {
  providersPhoneCalls,
  providersPhoneCallsDispositions,
  ProvidersPhoneCallsListResponse,
  ProvidersPhoneCallsCreateBodyParams,
  ProvidersPhoneCallsCreateResponse,
  ProvidersPhoneCallsPartialUpdateBodyParams,
  ProvidersPhoneCallsPartialUpdateResponse,
  ProvidersPhoneCallsDispositionsListResponse,
} from '@fireflyhealth/api'

/**
 * Type for a single phone call from the list response.
 * Uses snake_case field names matching the API.
 */
export type PhoneCall = ProvidersPhoneCallsListResponse['results'][number]

/**
 * Type for phone call direction, derived from the API schema.
 */
export type PhoneCallDirection = PhoneCall['direction']

/**
 * Phone call direction constants for use in UI logic.
 */
export const PhoneCallDirectionEnum = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
} as const satisfies Record<string, PhoneCallDirection>

/**
 * Type for a single phone call disposition from the list response.
 * Uses snake_case field names matching the API.
 */
export type PhoneCallDisposition = ProvidersPhoneCallsDispositionsListResponse['results'][number]

/**
 * Type for the phone call list response.
 */
type PhoneCallListResponse = PhoneCall[]

export const PhoneCallsKey = 'PhoneCalls'

/**
 * addPhoneCall
 * Creates a new phone call record using the generated API client.
 * @param payload - Phone call creation data (snake_case fields)
 * @returns Newly created phone call
 */
async function addPhoneCall(
  payload: ProvidersPhoneCallsCreateBodyParams,
): Promise<ProvidersPhoneCallsCreateResponse> {
  const response = await providersPhoneCalls.create({ provider_id: 'me' }, { data: payload })
  return response.data
}

export const useAddPhoneCall = () => {
  const client = useQueryClient()

  return useMutation(
    addPhoneCall,
    {
      onSuccess: (result: ProvidersPhoneCallsCreateResponse) => {
        // Invalidate chat queries to show the new phone call in the chat window
        if (result.person != null) {
          void client.invalidateQueries({ queryKey: ['chat', result.person] })
        }
        // Invalidate phone calls list so new call appears
        void client.invalidateQueries({ queryKey: [PhoneCallsKey] })
      },
    },
    { error: 'Failed to add phone call' },
  )
}

/**
 * updatePhoneCall
 * Updates an existing phone call record using the generated API client.
 * @param payload - Phone call update data with required id (snake_case fields)
 * @returns Updated phone call
 */
async function updatePhoneCall(
  payload: { id: number } & ProvidersPhoneCallsPartialUpdateBodyParams,
): Promise<ProvidersPhoneCallsPartialUpdateResponse> {
  const { id, ...updateData } = payload
  const response = await providersPhoneCalls.partialUpdate(
    { provider_id: 'me', id },
    { data: updateData },
  )
  return response.data
}

export const useUpdatePhoneCall = (personId: number | null) => {
  const client = useQueryClient()
  return useMutation(
    updatePhoneCall,
    {
      onSuccess: async _result => {
        // Invalidate chat queries to show the linked phone call in the chat window
        if (personId != null) {
          void client.invalidateQueries({ queryKey: ['chat', personId] })
        }
        await client.cancelQueries({ queryKey: [PhoneCallsKey] })
        await client.invalidateQueries({ queryKey: [PhoneCallsKey] })
      },
      onError: () => {
        // A link can fail because the call was already linked elsewhere (backend 409).
        // Refresh the unlinked-calls list so the now-stale call drops out and the
        // provider isn't left clicking a phantom row.
        void client.invalidateQueries({ queryKey: [PhoneCallsKey] })
      },
    },
    { error: 'Failed to update phone call' },
  )
}

/**
 * getPhoneCallsForProvider
 * Fetches phone calls for the current provider using the generated API client.
 * @param params - Query parameters for filtering phone calls
 * @returns Array of phone calls (snake_case fields)
 */
const getPhoneCallsForProvider = async (params: {
  handled_by?: number
  user?: number
  person_isnull?: boolean
  limit?: number
  offset?: number
}): Promise<PhoneCallListResponse> => {
  const response = await providersPhoneCalls.list({ provider_id: 'me' }, { params })
  return response.data.results || []
}

export const useGetPhoneCallsForProvider = (params: {
  handled_by?: number
  person?: number
  person_isnull?: boolean
  limit?: number
  offset?: number
}) => {
  return useQuery(
    [PhoneCallsKey, params],
    () => getPhoneCallsForProvider(params),
    {
      staleTime: 2 * 60 * 60 * 1000, // 2 hours
    },
    {
      error: 'Failed to fetch phone calls',
    },
  )
}

/**
 * listPhoneCallDispositions
 * Fetches phone call dispositions using the generated API client.
 * @returns Array of phone call dispositions (snake_case fields)
 */
async function listPhoneCallDispositions(): Promise<PhoneCallDisposition[]> {
  const response = await providersPhoneCallsDispositions.list({ provider_id: 'me' })
  return response.data.results
}

const listPhoneCallDispositionsKey = 'listPhoneCallDispositions'
export const usePhoneCallDispositions = () => {
  const { data, isPending: isLoading } = useQuery(
    [listPhoneCallDispositionsKey],
    listPhoneCallDispositions,
    {
      staleTime: Infinity,
    },
    {
      error: 'Failed to fetch phone call dispositions',
    },
  )

  return { result: data ?? [], isLoading }
}
